import { afterNextRender, Component, computed, inject, Injector, OnDestroy, OnInit, signal } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { Button } from 'primeng/button';
import { Timeline } from 'primeng/timeline';
import { Subscription } from 'rxjs';

import { EventosService } from '../../../core/services/eventos.service';
import { SkeletonBlockComponent } from '../../../shared/components/skeleton-block/skeleton-block.component';
import { Evento, IconoEvento, PublicoEvento } from '../../../shared/interfaces/evento.interface';

@Component({
  selector: 'app-eventos-lider',
  standalone: true,
  imports: [Button, Timeline, SkeletonBlockComponent],
  templateUrl: './eventos-lider.component.html',
  styleUrl: './eventos-lider.component.scss',
})
export class EventosLiderComponent implements OnInit, OnDestroy {
  private readonly eventosService = inject(EventosService);
  private readonly viewport = inject(ViewportScroller);
  private readonly injector = inject(Injector);
  private listado?: Subscription;
  private vivo = true;

  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly eventos = signal<Evento[]>([]);

  readonly linea = computed(() => ordenarLinea(this.eventos(), aYmd(inicioDeHoy())));

  ngOnInit(): void {
    this.cargar();
  }

  ngOnDestroy(): void {
    this.vivo = false;
    this.listado?.unsubscribe();
  }

  reintentar(): void {
    this.cargar();
  }

  esPasado(evento: Evento): boolean {
    return evento.fecha < aYmd(inicioDeHoy());
  }

  fechaCorta(evento: Evento): string {
    const fecha = ymdAFecha(evento.fecha);
    if (!fecha) {
      return evento.fecha;
    }
    const opciones: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' };
    if (fecha.getFullYear() !== inicioDeHoy().getFullYear()) {
      opciones.year = 'numeric';
    }
    return capitalizarMes(new Intl.DateTimeFormat('es-AR', opciones).format(fecha));
  }

  cuando(evento: Evento): string {
    return evento.conHorario && evento.horario ? evento.horario : 'Todo el día';
  }

  colorMarca(evento: Evento): string {
    return evento.color === 'blanco' ||
      evento.color === 'rojo' ||
      evento.color === 'amarillo' ||
      evento.color === 'verde'
      ? evento.color
      : 'primario';
  }

  claseIcono(evento: Evento): string {
    return iconoDe(evento.icono) ? `pi pi-${evento.icono}` : '';
  }

  detalle(evento: Evento): string {
    return textoPublico(evento.publico);
  }

  private cargar(): void {
    this.listado?.unsubscribe();
    this.loading.set(true);
    this.loadError.set(false);
    this.listado = this.eventosService.linea().subscribe({
      next: (respuesta) => {
        this.eventos.set(respuesta.data ?? []);
        this.loading.set(false);
        this.bajarAlFinal();
      },
      error: () => {
        this.loading.set(false);
        this.loadError.set(true);
      },
    });
  }

  private bajarAlFinal(): void {
    afterNextRender(() => {
      if (!this.vivo || this.linea().length === 0) {
        return;
      }
      const alto = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
      this.viewport.scrollToPosition([0, alto]);
    }, { injector: this.injector });
  }
}

function capitalizarMes(texto: string): string {
  return texto.replace(
    /\b(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)\b/i,
    (mes) => mes.charAt(0).toLocaleUpperCase('es-AR') + mes.slice(1),
  );
}

function iconoDe(value: string | null | undefined): IconoEvento {
  const permitidos: IconoEvento[] = [
    'calendar',
    'gift',
    'exclamation-triangle',
    'star',
    'heart',
    'users',
    'megaphone',
    'flag',
    'bell',
    'sparkles',
  ];
  return permitidos.includes(value as IconoEvento) ? (value as IconoEvento) : '';
}

function textoPublico(publico: PublicoEvento): string {
  if (publico === 'hombres') {
    return 'Para hombres';
  }
  if (publico === 'mujeres') {
    return 'Para mujeres';
  }
  return '';
}

const PASADOS_VISIBLES = 3;

function ordenarLinea(eventos: Evento[], hoy: string): Evento[] {
  const proximos = eventos.filter((evento) => evento.fecha >= hoy).sort(compararMasLejanoPrimero);
  const pasados = eventos
    .filter((evento) => evento.fecha < hoy)
    .sort(compararMasLejanoPrimero)
    .slice(0, PASADOS_VISIBLES);
  return [...proximos, ...pasados];
}

function compararMasLejanoPrimero(a: Evento, b: Evento): number {
  if (a.fecha !== b.fecha) {
    return a.fecha > b.fecha ? -1 : 1;
  }
  return compararMismoDia(a, b);
}

function compararMismoDia(a: Evento, b: Evento): number {
  if (a.conHorario !== b.conHorario) {
    return a.conHorario ? 1 : -1;
  }
  if (a.horario !== b.horario) {
    return a.horario < b.horario ? -1 : 1;
  }
  return a.nombre.localeCompare(b.nombre, 'es');
}

function inicioDeHoy(): Date {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  return hoy;
}

function aYmd(fecha: Date): string {
  const month = String(fecha.getMonth() + 1).padStart(2, '0');
  const day = String(fecha.getDate()).padStart(2, '0');
  return `${fecha.getFullYear()}-${month}-${day}`;
}

function ymdAFecha(ymd: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!match) {
    return null;
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}
